import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { library } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import useFetch from '../../hooks/useFetch';

library.add(fas);

const shortLabel = (str) => str;

const PDFAQ = () => {
  const authState = useSelector((state) => state.authReducer || {});
  const [fetchData] = useFetch();

  const userName = authState?.user?.name || "User";

  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeCategory, setActiveCategory] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [openFaqId, setOpenFaqId] = useState(null);

    useEffect(() => {
    setLoading(true);
    setError(null);

    const config = {
        url: "/faq/pdallfaqs",
        method: "GET",
        headers: {
            Authorization: authState.token,
        },
    };

    fetchData(config)
        .then((response) => {
            if (response?.success) {
                setFaqs(response.data || []);
            } else {
                setError("Couldn't load the FAQs right now. Give it another shot.");
            }
        })
        .catch((error) => {
            console.error("[PDFAQ]", error);
            setError("Couldn't load the FAQs right now. Give it another shot.");
        })
        .finally(() => {
            setLoading(false);
        });
}, [authState.token]);

  // Build ordered category list from catDisplaySN, "All" pinned first
  const categories = useMemo(() => {
    const map = new Map();
    faqs.forEach((f) => {
      if (!map.has(f.category)) {
        map.set(f.category, f.catDisplaySN ?? 999);
      }
    });
    const ordered = [...map.entries()]
      .sort((a, b) => a[1] - b[1])
      .map(([name]) => name);
    return ["All", ...ordered];
  }, [faqs]);

  const filteredFaqs = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return faqs.filter((f) => {
      const matchesCategory =
        activeCategory === "All" || f.category === activeCategory;
      const matchesSearch =
        !term ||
        f.faq.toLowerCase().includes(term) ||
        f.answer.toLowerCase().includes(term) ||
        f.category.toLowerCase().includes(term);
      return matchesCategory && matchesSearch;
    });
  }, [faqs, activeCategory, searchTerm]);

  // Group filtered FAQs by category, preserving display order
  const groupedFaqs = useMemo(() => {
    const groups = [];
    const indexByCategory = new Map();

    filteredFaqs.forEach((f) => {
      if (!indexByCategory.has(f.category)) {
        indexByCategory.set(f.category, groups.length);
        groups.push({
          category: f.category,
          catDisplaySN: f.catDisplaySN ?? 999,
          items: [],
        });
      }
      groups[indexByCategory.get(f.category)].items.push(f);
    });

    groups.sort((a, b) => a.catDisplaySN - b.catDisplaySN);
    groups.forEach((g) =>
      g.items.sort(
        (a, b) => (a.subcatDisplaySN || 0) - (b.subcatDisplaySN || 0)
      )
    );

    return groups;
  }, [filteredFaqs]);

  const toggleFaq = (faqid) => {
    setOpenFaqId((prev) => (prev === faqid ? null : faqid));
  };

  return (
    <div className="min-h-screen w-full bg-[#0F0B1E] text-[#F1EEFB] px-4 py-4">
      <div className="max-w-4xl mx-auto text-center mb-4">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#8B5CF6]/15 border border-[#8B5CF6]/40 px-3 py-1 text-[11px] md:text-xs font-medium tracking-wide text-[#C4B5FD] uppercase">
          <FontAwesomeIcon icon={["fas", "door-open"]} className="text-[10px]" />
          PurpleDoor · Think5Whys
        </span>

        <h1 className="mt-2 text-xl lg:text-2xl font-bold tracking-tight leading-tight">
          Got questions? <span className="text-[#A78BFA]">We've got whys.</span>{" "}
          🔮
        </h1>

        <p className="mt-3 text-sm lg:text-sm text-gray-300 max-w-2xl mx-auto">
          Everything about Think5Whys, the PurpleDoor module, jobs, contests,
          and how we build — no fluff, just answers.
        </p>
      </div>

      <div className="max-w-4xl mx-auto mb-2">
        <div className="relative">
          <FontAwesomeIcon
            icon={["fas", "magnifying-glass"]}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7C7593] text-sm"
          />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search FAQs… e.g. 'jobs', 'AI', 'women founders' 🔍"
            className="w-full rounded-xl bg-[#181329] border border-[#2E2749] focus:border-[#8B5CF6] focus:ring-2 focus:ring-[#8B5CF6]/30 outline-none transition-all py-3 pl-11 pr-4 text-sm lg:text-sm placeholder:text-[#7C7593]"
          />
        </div>
      </div>

      <div className="max-w-4xl mx-auto mb-2 px-4 border-b border-gray-400 rounded-xl p-2">
        <div className="flex gap-1 flex-wrap pb-2">
          {categories.map((cat) => {
            const isActive = activeCategory === cat;
            return (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`whitespace-nowrap rounded-md w-fit px-2 py-1 text-xs lg:text-sm font-medium border transition-all shrink-0 ${
                  isActive
                    ? "bg-[#8B5CF6] border-[#8B5CF6] text-white shadow-[0_0_18px_-2px_rgba(139,92,246,0.6)]"
                    : "bg-[#181329] border-[#2E2749] text-[#C7C2DA] hover:border-[#8B5CF6]/60 hover:text-white"
                }`}
              >
                {shortLabel(cat)}
              </button>
            );
          })}
        </div>
      </div>

      {/* ---------- States: loading / error / empty ---------- */}
      <div className="max-w-3xl mx-auto">
        {loading && (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="h-16 md:h-20 rounded-2xl bg-[#181329] border border-[#2E2749] animate-pulse"
              />
            ))}
          </div>
        )}

        {!loading && error && (
          <div className="text-center py-10 rounded-2xl border border-[#3A2A4D] bg-[#1C1430]">
            <FontAwesomeIcon
              icon={["fas", "triangle-exclamation"]}
              className="text-2xl text-[#F472B6] mb-3"
            />
            <p className="text-sm lg:text-sm text-[#E7E3F5]">{error}</p>
          </div>
        )}

        {!loading && !error && filteredFaqs.length === 0 && (
          <div className="text-center py-8">
            <p className="text-3xl mb-2">🕵️‍♀️</p>
            <p className="text-sm lg:text-sm text-[#A8A3B8]">
              No FAQs match "{searchTerm}" in {activeCategory}. Try another
              keyword or category.
            </p>
          </div>
        )}

        {/* ---------- Grouped FAQ list ---------- */}
        {!loading &&
          !error &&
          groupedFaqs.map((group) => (
            <section key={group.category} className="mb-2">
              <h2 className="flex items-center gap-2 text-sm lg:text-sm font-semibold text-[#C4B5FD] uppercase tracking-wide mb-3 px-1">
                <span className="h-1.5 w-1.5 rounded-full bg-[#8B5CF6]" />
                {group.category}
              </h2>

              <div className="space-y-1">
                {group.items.map((item) => {
                  const isOpen = openFaqId === item.faqid;
                  return (
                    <div
                      key={item.faqid}
                      className={`rounded-xl border transition-all overflow-hidden ${
                        item.isSpecial
                          ? "border-[#8B5CF6]/50 bg-gradient-to-br from-[#1E1638] to-[#181329]"
                          : "border-[#2E2749] bg-[#181329]"
                      } ${isOpen ? "ring-1 ring-[#8B5CF6]/40" : ""}`}
                    >
                      <button
                        onClick={() => toggleFaq(item.faqid)}
                        className="flex items-center gap-3 w-fit px-2 py-1"
                        aria-expanded={isOpen}
                      >
                        <span
                          className={`flex items-center justify-center h-9 w-9 md:h-10 md:w-10 rounded-xl shrink-0 text-sm lg:text-sm ${
                            item.isSpecial
                              ? "bg-[#8B5CF6] text-white"
                              : "bg-[#2E2749] text-[#C4B5FD]"
                          }`}
                        >
                          <FontAwesomeIcon
                            icon={["fas", item.iconName || "circle-question"]}
                          />
                        </span>

                        <span className="flex-1 text-sm lg:text-sm font-medium text-[#F1EEFB] leading-snug">
                          {item.faq}
                          {item.isSpecial && (
                            <span className="ml-2 align-middle">✨</span>
                          )}
                        </span>

                        <FontAwesomeIcon
                          icon={["fas", "chevron-down"]}
                          className={`text-[#7C7593] text-xs shrink-0 transition-transform duration-300 ${
                            isOpen ? "rotate-180 text-[#A78BFA]" : ""
                          }`}
                        />
                      </button>

                      <div
                        className={`grid transition-all duration-300 ease-in-out ${
                          isOpen
                            ? "grid-rows-[1fr] opacity-100"
                            : "grid-rows-[0fr] opacity-0"
                        }`}
                      >
                        <div className="overflow-hidden">
                          <p className="px-4 pb-2 pl-[52px] lg:pl-[56px] pr-4 text-[13px] lg:text-sm text-[#B8B2CC] leading-relaxed">
                            {item.answer}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
      </div>
    </div>
  );
};

export default PDFAQ;